<template>
  <div class="fcom_month_calendar" role="grid" :aria-label="__('month_view')">
    <!-- Weekday header row -->
    <div class="fcom_month_header" role="row">
      <div
        v-for="day in weekdayLabels"
        :key="day.key"
        class="fcom_month_header-cell"
        role="columnheader"
      >
        <span class="fcom_month_header-short">{{ day.short }}</span>
        <span class="fcom_month_header-full">{{ day.full }}</span>
      </div>
    </div>

    <!-- Month grid -->
    <div class="fcom_month_grid" role="rowgroup">
      <TransitionGroup name="fcom_month_slide">
        <div
          v-for="(week, wi) in monthGrid"
          :key="`${currentYearMonth}-week-${wi}`"
          class="fcom_month_week"
          role="row"
        >
          <div
            v-for="cell in week"
            :key="cell.dateString"
            :class="[
              'fcom_month_day',
              {
                'is-today': cell.isToday,
                'is-current-month': cell.isCurrentMonth,
                'is-other-month': !cell.isCurrentMonth,
              },
            ]"
            role="gridcell"
            :aria-selected="cell.isToday"
            :aria-label="cell.date.toFormat('EEEE, MMMM d')"
            @click="$emit('select-date', cell.date)"
          >
            <div class="fcom_month_day-header">
              <span class="fcom_month_day-number">{{ cell.day }}</span>
              <span v-if="cell.isToday" class="fcom_month_day-today-indicator">{{ __('today') }}</span>
            </div>

            <div class="fcom_month_day-events">
              <div
                v-for="ev in eventsForDate(cell.dateString).slice(0, maxEventsVisible)"
                :key="ev.id"
                class="fcom_month_event_wrapper"
              >
                <CalendarEvent
                  :event="ev"
                  :show-time="false"
                  @click="$emit('select-event', ev)"
                  @hover="(p) => $emit('event-hover', { ...p, source: 'month' })"
                  @leave="$emit('event-leave')"
                />
              </div>

              <div
                v-if="eventsForDate(cell.dateString).length > maxEventsVisible"
                class="fcom_month_more_events"
                @click.stop="$emit('select-date', cell.date)"
              >
                +{{ eventsForDate(cell.dateString).length - maxEventsVisible }} {{ __('more') }}
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { __ } from '@/utils/i18n.js'
import {
  buildMonthGrid,
  indexEventsByDate,
  getEventsForDate,
  buildWeekdayLabels,
} from '@/utils/calendar.js'
import CalendarEvent from './CalendarEvent.vue'

const props = defineProps({
  currentDate:      { type: Object, required: true },
  events:           { type: Array,  default: () => [] },
  weekStartDay:     { type: String, default: 'monday' },
  maxEventsVisible: { type: Number, default: 3 },
})

defineEmits(['select-date', 'select-event', 'event-hover', 'event-leave'])

watch(() => props.events, () => {}, { deep: true })

const indexedEvents  = computed(() => indexEventsByDate(props.events))
const monthGrid      = computed(() => buildMonthGrid(props.currentDate, props.weekStartDay))
const currentYearMonth = computed(() => props.currentDate.toFormat('yyyy-MM'))
const weekdayLabels  = computed(() => buildWeekdayLabels(props.weekStartDay))

function eventsForDate(dateString) {
  return getEventsForDate(indexedEvents.value, dateString)
}
</script>
